/**
 * ייצוא ארכיון שיחה לקובץ Excel (.xlsx) עם תמיכה מלאה בעברית (Unicode / OOXML).
 * לא משנה מצב Redux / הודעות / סוקט — קריאה בלבד + הורדת קובץ.
 */

import * as XLSX from 'xlsx';
import { getChatSearchMessagesArr } from 'api/chat';
import { store } from 'store';
import type { ChatUserModel } from 'types/chat-users/chat-user';
import type { HistoryMessage, MessageModel } from 'types/chat/messages/message';
import { normalizeChatMessageFlow } from 'types/chat/messages/common';
import { appendChatExportDownloadEntry } from 'utils/chat-export-downloads-journal';
import { phoneGlobalRegex } from 'utils/regexes';

export type ExportChatToExcelParams = {
  chatUser: ChatUserModel;
  /** אם קיים — משמש כגיבוי כשה-API מחזיר ריק */
  fallbackMessages?: Array<HistoryMessage | MessageModel>;
};

export type ExportChatToExcelResult = {
  fileName: string;
  messageCount: number;
  contactName: string;
  contactPhone: string;
};

const SKIP_FILE_TYPES = new Set(['buffer']);

const MEDIA_TYPE_HE: Record<string, string> = {
  text: 'טקסט',
  image: 'תמונה',
  video: 'וידאו',
  audio: 'אודיו',
  document: 'מסמך',
  docx: 'מסמך',
  pdf: 'PDF',
  xlsx: 'Excel',
  zip: 'ארכיון',
  gif: 'GIF',
  sticker: 'סטיקר',
  template: 'תבנית',
  note: 'הערה פנימית',
  location: 'מיקום',
  contacts: 'איש קשר',
  buttons: 'כפתורים',
  order: 'הזמנה',
  catalog: 'קטלוג',
  reply: 'תשובה',
  reaction: 'תגובה',
  info: 'מערכת',
  error: 'שגיאה',
};

function formatDisplayPhone(raw?: string | null): string {
  if (!raw) return '';
  const clearPhone = String(raw).replaceAll(/\D/g, '');
  if (!clearPhone) return String(raw);
  return phoneGlobalRegex.test(clearPhone) ? `0${clearPhone.slice(3)}` : clearPhone;
}

function sanitizeFileNamePart(value: string): string {
  return value
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

function parseMessageDate(msg: HistoryMessage | MessageModel): Date | null {
  const historyExtra = msg as HistoryMessage;
  const candidates = [
    msg.DateCreatedOriginal,
    msg.DateCreateRaw,
    msg.DateCreate,
    historyExtra.DateCreateSystem,
    msg.createdAt,
  ];
  for (const c of candidates) {
    if (!c) continue;
    const d = new Date(c);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDateHe(d: Date | null): string {
  if (!d) return '';
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function formatTimeHe(d: Date | null): string {
  if (!d) return '';
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
}

function formatDateTimeHe(d: Date): string {
  return `${formatDateHe(d)} ${formatTimeHe(d)}`;
}

function resolveMessageBody(msg: HistoryMessage | MessageModel): string {
  const text =
    (typeof msg.FormattedMessage === 'string' && msg.FormattedMessage.trim()) ||
    (typeof msg.Message === 'string' && msg.Message.trim()) ||
    (typeof msg.text === 'string' && msg.text.trim()) ||
    '';

  if (text) return text;

  const ft = String(msg.FileType || '').toLowerCase();
  if (ft && MEDIA_TYPE_HE[ft]) return `[${MEDIA_TYPE_HE[ft]}]`;
  if (msg.File) return '[קובץ מצורף]';
  return '';
}

function resolveMessageTypeLabel(msg: HistoryMessage | MessageModel): string {
  const ft = String(msg.FileType || 'text').toLowerCase();
  return MEDIA_TYPE_HE[ft] || ft || 'טקסט';
}

function isExportableMessage(msg: HistoryMessage | MessageModel): boolean {
  const ft = String(msg.FileType || '').toLowerCase();
  if (SKIP_FILE_TYPES.has(ft)) return false;
  return true;
}

function sortMessagesChronologically(
  messages: Array<HistoryMessage | MessageModel>,
): Array<HistoryMessage | MessageModel> {
  return [...messages].sort((a, b) => {
    const da = parseMessageDate(a)?.getTime() ?? 0;
    const db = parseMessageDate(b)?.getTime() ?? 0;
    if (da !== db) return da - db;
    const ia = a.Id ?? 0;
    const ib = b.Id ?? 0;
    return Number(ia) - Number(ib);
  });
}

function getFallbackMessagesFromStore(phone: string): Array<HistoryMessage | MessageModel> {
  try {
    const saved = store.getState().chat.chatUsersSavedMessages || [];
    const row = saved.find((x) => x.id === phone);
    return row?.messages ? [...row.messages] : [];
  } catch {
    return [];
  }
}

function applyRtlSheet(ws: XLSX.WorkSheet): void {
  // SheetJS: תצוגת גיליון מימין לשמאל (Excel)
  (ws as XLSX.WorkSheet & { '!views'?: Array<{ rightToLeft?: boolean }> })['!views'] = [
    { rightToLeft: true },
  ];
}

function setColWidths(ws: XLSX.WorkSheet, widths: number[]): void {
  ws['!cols'] = widths.map((wch) => ({ wch }));
}

/**
 * מוריד קובץ Excel של ארכיון השיחה ורושם רשומה ביומן ההורדות (localStorage).
 */
export async function exportChatConversationToExcel(
  params: ExportChatToExcelParams,
): Promise<ExportChatToExcelResult> {
  const { chatUser } = params;
  const phoneRaw = chatUser.UserDisplayPhoneNumber || '';
  if (!phoneRaw) {
    throw new Error('חסר מספר טלפון לאיש הקשר');
  }

  const contactName = (chatUser.FullName || chatUser.UserDisplayPhoneNumber || 'איש קשר').trim();
  const contactPhone = formatDisplayPhone(phoneRaw) || phoneRaw;

  let messages: Array<HistoryMessage | MessageModel> = [];
  try {
    messages = await getChatSearchMessagesArr(phoneRaw);
  } catch {
    messages = [];
  }

  if (!messages.length) {
    const fallback =
      params.fallbackMessages?.length
        ? params.fallbackMessages
        : getFallbackMessagesFromStore(phoneRaw);
    messages = fallback;
  }

  const exportable = sortMessagesChronologically(messages.filter(isExportableMessage));
  if (!exportable.length) {
    throw new Error('אין הודעות לייצוא בשיחה זו');
  }

  const company = store.getState().company;
  const user = store.getState().user;
  const companyName = company?.CompanyName?.trim() || 'העסק';
  const companyPhone = formatDisplayPhone(company?.DisplayPhoneNumber) || company?.DisplayPhoneNumber || '';
  const exporterName =
    [user?.FirstName, user?.LastName].filter(Boolean).join(' ').trim() ||
    user?.FullName?.trim() ||
    user?.Email?.trim() ||
    'משתמש';

  const exportedAt = new Date();
  const fileName = `${sanitizeFileNamePart(contactName)} - ${sanitizeFileNamePart(contactPhone)}.xlsx`;

  const metaRows: (string | number)[][] = [
    ['ארכיון שיחה — ייצוא Excel'],
    [],
    ['שם איש קשר', contactName],
    ['מספר טלפון', contactPhone],
    ['חברה', companyName],
    ['מספר העסק', companyPhone],
    ['יוצא על ידי', exporterName],
    ['תאריך ושעת ייצוא', formatDateTimeHe(exportedAt)],
    ['מספר הודעות', exportable.length],
  ];

  const header = [
    '#',
    'תאריך',
    'שעה',
    'כיוון',
    'שולח',
    'מקבל',
    'סוג הודעה',
    'תוכן',
    'קישור לקובץ',
    'סטטוס',
    'נציג',
    'מזהה הודעה',
  ];

  const dataRows: (string | number)[][] = [header];

  exportable.forEach((msg, index) => {
    const direction = normalizeChatMessageFlow(msg.Type);
    const isOut = direction === 'outcoming';
    const when = parseMessageDate(msg);
    const agentName = (msg.GlobalUserName || '').trim();

    const sender = isOut
      ? agentName || companyName || companyPhone || 'העסק'
      : contactName || contactPhone;
    const receiver = isOut
      ? contactName || contactPhone
      : companyName || companyPhone || 'העסק';

    dataRows.push([
      index + 1,
      formatDateHe(when),
      formatTimeHe(when),
      isOut ? 'יוצא' : 'נכנס',
      sender,
      receiver,
      resolveMessageTypeLabel(msg),
      resolveMessageBody(msg),
      typeof msg.File === 'string' ? msg.File : '',
      (msg.StatusHeb || msg.Status || '').toString(),
      agentName,
      msg.MessageId || msg.messageId || msg.Id || '',
    ]);
  });

  const wb = XLSX.utils.book_new();

  const wsMeta = XLSX.utils.aoa_to_sheet(metaRows);
  applyRtlSheet(wsMeta);
  setColWidths(wsMeta, [22, 48]);
  XLSX.utils.book_append_sheet(wb, wsMeta, 'פרטי ייצוא');

  const wsArchive = XLSX.utils.aoa_to_sheet(dataRows);
  applyRtlSheet(wsArchive);
  setColWidths(wsArchive, [5, 12, 10, 8, 22, 22, 12, 55, 28, 12, 18, 22]);
  XLSX.utils.book_append_sheet(wb, wsArchive, 'ארכיון שיחה');

  XLSX.writeFile(wb, fileName, { bookType: 'xlsx', compression: true });

  appendChatExportDownloadEntry({
    exporterUserId: user?.Udid ?? null,
    exporterName,
    contactName,
    contactPhone,
    fileName,
    messageCount: exportable.length,
    exportedAt: exportedAt.toISOString(),
  });

  return {
    fileName,
    messageCount: exportable.length,
    contactName,
    contactPhone,
  };
}
